import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Save, X, AlertTriangle, ShieldAlert } from 'lucide-react';

const StrategicSaveModal = ({ isOpen, onClose, onConfirm, currentVersion }) => {
    const { t } = useTranslation();
    const [justification, setJustification] = useState('');
    const [error, setError] = useState('');

    if (!isOpen) return null;

    // Calculate next major version
    const majorVersion = parseInt(currentVersion?.split('.')[0] || '1');
    const nextVersion = `${majorVersion + 1}.0`;

    const handleSubmit = () => {
        if (!justification.trim()) {
            setError(t('versionManagement.strategicUpdate.justificationRequired', 'Justification requise'));
            return;
        }
        onConfirm(justification);
        setJustification('');
        setError('');
    };

    return ReactDOM.createPortal(
        <AnimatePresence>
            <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-lg overflow-hidden border border-slate-200 dark:border-slate-700"
                >
                    {/* Header */}
                    <div className="bg-gradient-to-r from-orange-600 to-red-600 p-6 text-white">
                        <div className="flex justify-between items-start">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-white/20 rounded-lg">
                                    <ShieldAlert className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold">
                                        {t('versionManagement.strategicUpdate.modalTitle', 'Sauvegarde Stratégique')}
                                    </h2>
                                    <p className="text-orange-100 text-sm mt-1">
                                        {t('versionManagement.strategicUpdate.buttonTitle', 'Créer une version majeure')}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={onClose}
                                className="text-white/70 hover:text-white p-1 rounded-full hover:bg-white/10 transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                    </div>

                    <div className="p-6">
                        {/* Version Change Display */}
                        <div className="flex items-center justify-center gap-4 mb-8 bg-slate-50 dark:bg-slate-900/50 p-4 rounded-lg border border-slate-200 dark:border-slate-700 border-dashed">
                            <div className="text-center">
                                <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                                    {t('common.current', 'Actuelle')}
                                </span>
                                <div className="text-2xl font-bold text-slate-600 dark:text-slate-400">v{currentVersion || '1.0'}</div>
                            </div>

                            <div className="h-px w-12 bg-slate-300 dark:bg-slate-600 relative">
                                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-slate-400">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14" /><path d="m12 5 7 7-7 7" /></svg>
                                </div>
                            </div>

                            <div className="text-center">
                                <span className="text-xs font-medium text-orange-600 uppercase tracking-wider">
                                    {t('common.new', 'Nouvelle')}
                                </span>
                                <div className="text-2xl font-bold text-orange-600">v{nextVersion}</div>
                            </div>
                        </div>

                        {/* Warning Message */}
                        <div className="mb-6 flex gap-3 p-3 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200 rounded-lg text-sm">
                            <AlertTriangle className="w-5 h-5 shrink-0" />
                            <p>
                                {t('versionManagement.strategicUpdate.description',
                                    'Cette action créera une copie complète (snapshot) de l\'analyse actuelle et incrémentera le numéro de version majeure. Utilisez cette fonction pour marquer des jalons importants.')}
                            </p>
                        </div>

                        {/* Justification Input */}
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                                {t('versionManagement.strategicUpdate.justificationLabel', 'Justification de la version')} <span className="text-red-500">*</span>
                            </label>
                            <textarea
                                value={justification}
                                onChange={(e) => setJustification(e.target.value)}
                                placeholder={t('versionManagement.strategicUpdate.justificationPlaceholder', 'Ex: Validation fin d\'atelier 1, avant présentation au CODIR...')}
                                className="w-full px-4 py-3 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition-all resize-none h-32"
                                autoFocus
                            />
                            {error && (
                                <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
                                    <AlertTriangle className="w-3 h-3" />
                                    {error}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="p-6 bg-slate-50 dark:bg-slate-900/50 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-3">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-slate-600 dark:text-slate-400 font-medium hover:text-slate-900 dark:hover:text-slate-200 transition-colors"
                        >
                            {t('versionManagement.strategicUpdate.cancelButton', 'Annuler')}
                        </button>
                        <button
                            onClick={handleSubmit}
                            className="px-6 py-2 bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-lg font-medium shadow-lg shadow-orange-500/30 hover:shadow-orange-500/50 hover:from-orange-500 hover:to-red-500 transition-all flex items-center gap-2"
                        >
                            <Save className="w-4 h-4" />
                            {t('versionManagement.strategicUpdate.confirmButton', 'Créer la version')}
                        </button>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>,
        document.body
    );
};

export default StrategicSaveModal;
